import { Check, Copy, Clock, Sparkles, MessageSquare, Loader2, Download, Play, Pause, Volume2, ChevronDown } from "lucide-react";
import { useState, useRef, useEffect } from "react";
import { useTranslation } from "react-i18next";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { SectionCard } from "@/components/ui/section-card";
import { Badge } from "@/components/ui/badge";
import { Slider } from "@/components/ui/slider";
import { toast } from "@/hooks/use-toast";
import type { GenerationState } from "@/types/api";

interface GeneratedMessageProps {
  generationState: GenerationState;
  onMessageEdit: (message: string) => void;
  onRegenerateAudio?: (textOverride?: string) => void;
  onDownloadRawAudio?: () => void;
  isGenerating?: boolean;
  isGeneratingAudio?: boolean;
  profileUrl?: string;
  projectId?: number;
  sender?: string;
  audioText?: string;
  audioTextLoading?: boolean;
  audioTextError?: string | null;
  onAudioTextChange?: (text: string) => void;
  onPrepareAudioText?: (text?: string) => void;
  onUseLocalAudioText?: () => void;
  onDownloadAudioAtSpeed?: (speed: number) => Promise<void>;
  compact?: boolean;
}

export function GeneratedMessage({
  generationState,
  onMessageEdit,
  onRegenerateAudio,
  isGenerating = false,
  isGeneratingAudio = false,
  audioText,
  audioTextLoading = false,
  audioTextError = null,
  onAudioTextChange,
  onPrepareAudioText,
  onUseLocalAudioText,
  onDownloadAudioAtSpeed,
  compact = false,
}: GeneratedMessageProps) {
  const { t } = useTranslation('generator');
  const [copied, setCopied] = useState(false);
  const [copiedSection, setCopiedSection] = useState<number | null>(null);
  const [sectionsOpen, setSectionsOpen] = useState(false);
  const [audioOpen, setAudioOpen] = useState(false);
  const audioAutoFetchedRef = useRef(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [playbackRate, setPlaybackRate] = useState(1.5);
  const [downloadingSpeed, setDownloadingSpeed] = useState<number | null>(null);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const { status, result, error, generationTime } = generationState;
  const displayedMessage = result?.generated_message || "";

  // Reset auto-fetch flag when message changes
  useEffect(() => {
    audioAutoFetchedRef.current = false;
  }, [displayedMessage]);

  // Auto-fetch audio version on first expand
  useEffect(() => {
    if (
      audioOpen &&
      !audioAutoFetchedRef.current &&
      !audioText &&
      !audioTextLoading &&
      !audioTextError &&
      displayedMessage &&
      onPrepareAudioText
    ) {
      audioAutoFetchedRef.current = true;
      onPrepareAudioText(displayedMessage);
    }
  }, [audioOpen, audioText, audioTextLoading, audioTextError, displayedMessage, onPrepareAudioText]);

  useEffect(() => {
    if (result?.audioUrl && audioRef.current) {
      audioRef.current.src = result.audioUrl;
      setCurrentTime(0);
      setIsPlaying(false);
    }
  }, [result?.audioUrl]);

  useEffect(() => {
    if (audioRef.current) audioRef.current.playbackRate = playbackRate;
  }, [playbackRate]);

  const formatTime = (time: number) => {
    const minutes = Math.floor(time / 60);
    const seconds = Math.floor(time % 60);
    return `${minutes}:${seconds.toString().padStart(2, '0')}`;
  };

  const handlePlayPause = () => {
    if (!audioRef.current) return;
    if (isPlaying) audioRef.current.pause();
    else audioRef.current.play();
    setIsPlaying(!isPlaying);
  };

  const handleTimeUpdate = () => {
    if (audioRef.current) setCurrentTime(audioRef.current.currentTime);
  };

  const handleLoadedMetadata = () => {
    if (audioRef.current) {
      setDuration(audioRef.current.duration);
      audioRef.current.playbackRate = playbackRate;
    }
  };

  const togglePlaybackRate = () => setPlaybackRate(prev => prev === 1.5 ? 1.0 : 1.5);

  const handleSeek = (value: number[]) => {
    if (audioRef.current) {
      audioRef.current.currentTime = value[0];
      setCurrentTime(value[0]);
    }
  };

  const handleEnded = () => {
    setIsPlaying(false);
    setCurrentTime(0);
  };

  const handleCopy = async () => {
    if (!displayedMessage) return;
    await navigator.clipboard.writeText(displayedMessage);
    setCopied(true);
    toast({
      title: t('generatedMessage.copy'),
      description: t('generatedMessage.copiedToClipboard'),
    });
    setTimeout(() => setCopied(false), 2000);
  };

  const handleDownloadAudio = () => {
    if (result?.audioUrl) {
      const link = document.createElement('a');
      link.href = result.audioUrl;
      link.download = `wiadomosc-audio-${Date.now()}.mp4`;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      toast({
        title: t('generatedMessage.downloadMp4'),
        description: t('generatedMessage.downloadedAudio'),
      });
    }
  };

  const handleDownloadAudioAtSpeed = async (speed: number) => {
    if (!onDownloadAudioAtSpeed || downloadingSpeed !== null) return;
    setDownloadingSpeed(speed);
    try {
      await onDownloadAudioAtSpeed(speed);
      toast({
        title: t('generatedMessage.downloadSpeed', { speed }),
        description: t('generatedMessage.downloadedAudio'),
      });
    } catch (err) {
      console.error('Speed download failed:', err);
      toast({
        title: t('generatedMessage.speedDownloadError'),
        description: err instanceof Error ? err.message : String(err),
        variant: 'destructive',
      });
    } finally {
      setDownloadingSpeed(null);
    }
  };

  const sections = result
    ? [result.section1, result.section2, result.section3].filter(Boolean) as string[]
    : [];

  return (
    <SectionCard
      icon={Sparkles}
      title={t('generatedMessage.title')}
      actions={
        generationTime ? (
          <Badge variant="outline" className="gap-1">
            <Clock className="h-3 w-3" />
            {generationTime}ms
          </Badge>
        ) : undefined
      }
    >
      <div className="space-y-4">
        {status === "pending" && (
          <div className="flex min-h-[200px] flex-col items-center justify-center gap-4 rounded-lg border border-border bg-secondary/20">
            <div className="h-8 w-8 animate-spin rounded-full border-2 border-primary border-t-transparent" />
            <p className="text-muted-foreground">{t('generatedMessage.pending')}</p>
          </div>
        )}

        {status === "error" && (
          <div className="rounded-lg border border-destructive/50 bg-destructive/10 p-4">
            <p className="text-destructive">{error || t('generatedMessage.errorDefault')}</p>
          </div>
        )}

        {status === "success" && result && (
          <>
            {!compact && (
              <>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-muted-foreground">
                    {t('generatedMessage.generatedAt', { time: new Date().toLocaleString() })}
                  </span>
                  <Button variant="outline" size="sm" onClick={handleCopy}>
                    {copied ? <Check className="mr-2 h-3 w-3" /> : <Copy className="mr-2 h-3 w-3" />}
                    {t('generatedMessage.copy')}
                  </Button>
                </div>

                <Textarea
                  className="min-h-[200px] resize-y"
                  value={displayedMessage}
                  onChange={(e) => onMessageEdit(e.target.value)}
                />
              </>
            )}

            {/* Audio version (TTS cleanup preview) */}
            {(() => {
              const originalLen = displayedMessage.length;
              const audioLen = (audioText ?? "").length;
              const diff = audioLen - originalLen;
              const diffLabel = !audioText
                ? ''
                : diff === 0
                  ? t('audioVersion.charDiffSame')
                  : diff < 0
                    ? t('audioVersion.charDiffShorter', { count: Math.abs(diff) })
                    : t('audioVersion.charDiffLonger', { count: diff });
              return (
                <div className="rounded-lg border border-border">
                  <button
                    type="button"
                    className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium hover:bg-secondary/30 transition-colors"
                    onClick={() => setAudioOpen((v) => !v)}
                    aria-expanded={audioOpen}
                  >
                    <span className="flex items-center gap-2">
                      <Sparkles className="h-4 w-4" />
                      {t('audioVersion.title')}
                      {audioText && !audioTextLoading && !audioTextError && (
                        <span className="text-xs text-muted-foreground font-normal">({diffLabel})</span>
                      )}
                    </span>
                    <ChevronDown className={`h-4 w-4 transition-transform ${audioOpen ? 'rotate-180' : ''}`} />
                  </button>
                  {audioOpen && (
                    <div className="space-y-1 border-t border-border p-3 bg-secondary/10">
                      {audioTextLoading ? (
                        <div className="flex items-center gap-2 py-6 text-sm text-muted-foreground justify-center">
                          <Loader2 className="h-4 w-4 animate-spin" />
                          {t('audioVersion.loading')}
                        </div>
                      ) : audioTextError ? (
                        <div className="space-y-2">
                          <div className="rounded-md border border-destructive/40 bg-destructive/10 p-2 text-xs text-destructive">
                            {audioTextError}
                          </div>
                          <div className="flex flex-wrap gap-2">
                            {onPrepareAudioText && (
                              <Button size="sm" variant="outline" onClick={() => onPrepareAudioText(displayedMessage)}>
                                {t('audioVersion.retry')}
                              </Button>
                            )}
                            {onUseLocalAudioText && (
                              <Button size="sm" variant="secondary" onClick={onUseLocalAudioText}>
                                {t('audioVersion.useLocalFallback')}
                              </Button>
                            )}
                          </div>
                        </div>
                      ) : audioText ? (
                        <>
                          <div className="text-xs text-muted-foreground mb-1">
                            {t('generatedMessage.charCount')} <span className="font-medium text-foreground">{audioLen}</span>
                          </div>
                          <Textarea
                            className="min-h-[160px] resize-y font-mono text-sm"
                            value={audioText}
                            onChange={(e) => onAudioTextChange?.(e.target.value)}
                          />
                        </>
                      ) : (
                        onPrepareAudioText && (
                          <Button size="sm" variant="outline" className="w-full gap-2" onClick={() => onPrepareAudioText(displayedMessage)}>
                            <Sparkles className="h-3 w-3" />
                            {t('audioVersion.prepare')}
                          </Button>
                        )
                      )}
                    </div>
                  )}
                </div>
              );
            })()}

            {onRegenerateAudio && (
              <Button
                variant="outline"
                className="w-full gap-2"
                onClick={() => onRegenerateAudio(audioText)}
                disabled={isGeneratingAudio || audioTextLoading}
              >
                {isGeneratingAudio ? (
                  <>
                    <Loader2 className="h-4 w-4 animate-spin" />
                    {t('generatedMessage.generatingAudio')}
                  </>
                ) : (
                  <>
                    <Volume2 className="h-4 w-4" />
                    {t('config.generateAudio')}
                  </>
                )}
              </Button>
            )}

            {result?.audioUrl && (
              <div className="mt-4 space-y-3">
                <audio
                  ref={audioRef}
                  src={result.audioUrl}
                  onTimeUpdate={handleTimeUpdate}
                  onLoadedMetadata={handleLoadedMetadata}
                  onEnded={handleEnded}
                  className="hidden"
                />

                <div className="flex items-center gap-3 p-3 rounded-lg border border-border bg-secondary/30">
                  <Button variant="outline" size="icon" className="h-10 w-10 shrink-0" onClick={handlePlayPause}>
                    {isPlaying ? <Pause className="h-4 w-4" /> : <Play className="h-4 w-4 ml-0.5" />}
                  </Button>
                  <div className="flex-1 space-y-1">
                    <Slider value={[currentTime]} max={duration || 100} step={0.1} onValueChange={handleSeek} className="cursor-pointer" />
                    <div className="flex justify-between text-xs text-muted-foreground">
                      <span>{formatTime(currentTime)}</span>
                      <span>{formatTime(duration)}</span>
                    </div>
                  </div>
                  <Button variant="ghost" size="sm" className="h-8 px-2 text-xs font-mono shrink-0" onClick={togglePlaybackRate}>
                    {playbackRate}x
                  </Button>
                  <Volume2 className="h-4 w-4 text-muted-foreground shrink-0" />
                </div>

                <Button variant="outline" className="w-full gap-2" onClick={handleDownloadAudio} disabled={downloadingSpeed !== null}>
                  <Download className="h-4 w-4" />
                  {t('generatedMessage.downloadMp4')}
                </Button>
                <div className="grid grid-cols-2 gap-2">
                  {[1.2, 1.5].map((speed) => {
                    const isBusy = downloadingSpeed === speed;
                    return (
                      <Button
                        key={speed}
                        variant="outline"
                        className="w-full gap-2"
                        onClick={() => handleDownloadAudioAtSpeed(speed)}
                        disabled={downloadingSpeed !== null}
                      >
                        {isBusy ? (
                          <>
                            <Loader2 className="h-4 w-4 animate-spin" />
                            {t('generatedMessage.preparingSpeed', { speed })}
                          </>
                        ) : (
                          <>
                            <Download className="h-4 w-4" />
                            {t('generatedMessage.downloadSpeed', { speed })}
                          </>
                        )}
                      </Button>
                    );
                  })}
                </div>
              </div>
            )}

            {sections.length > 0 && (
              <div className="rounded-lg border border-border">
                <button
                  type="button"
                  className="flex w-full items-center justify-between gap-2 px-3 py-2 text-sm font-medium hover:bg-secondary/30 transition-colors"
                  onClick={() => setSectionsOpen((v) => !v)}
                  aria-expanded={sectionsOpen}
                >
                  <span className="flex items-center gap-2">
                    <MessageSquare className="h-4 w-4" />
                    {t('generatedMessage.sectionsToggle', { defaultValue: 'Sekcje wiadomości (1/2/3)' })}
                  </span>
                  <ChevronDown className={`h-4 w-4 transition-transform ${sectionsOpen ? 'rotate-180' : ''}`} />
                </button>
                {sectionsOpen && (
                  <div className="space-y-3 border-t border-border p-3">
                    {[1, 2, 3].map((num) => {
                      const sectionKey = `section${num}` as 'section1' | 'section2' | 'section3';
                      const sectionValue = (result as any)?.[sectionKey] as string | undefined;
                      if (!sectionValue) return null;
                      return (
                        <div key={num} className="space-y-1">
                          <div className="flex items-center justify-between">
                            <span className="text-xs font-medium text-muted-foreground">
                              {t('generatedMessage.section', { number: num })}
                            </span>
                            <div className="flex items-center gap-2">
                              <span className="text-xs text-muted-foreground">{sectionValue.length}</span>
                              <Button
                                variant="ghost"
                                size="sm"
                                className={`h-6 px-2 text-xs transition-colors ${copiedSection === num ? 'bg-green-500/20 text-green-600 hover:bg-green-500/30 hover:text-green-600' : ''}`}
                                onClick={async () => {
                                  await navigator.clipboard.writeText(sectionValue);
                                  setCopiedSection(num);
                                  toast({
                                    title: t('generatedMessage.copySection'),
                                    description: t('generatedMessage.sectionCopied'),
                                  });
                                  setTimeout(() => setCopiedSection(null), 2000);
                                }}
                              >
                                {copiedSection === num ? <Check className="h-3 w-3 mr-1" /> : <Copy className="h-3 w-3 mr-1" />}
                                {t('generatedMessage.copySection')}
                              </Button>
                            </div>
                          </div>
                          <Textarea value={sectionValue} readOnly />
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            )}
          </>
        )}
      </div>
    </SectionCard>
  );
}
